import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  X,
  MapPin,
  Clock,
  Briefcase,
  ArrowUpRight,
  Share2,
  Linkedin,
  Facebook,
  Mail,
  MessageCircle,
  Link as LinkIcon,
  Check,
} from "lucide-react";
import JobDescription from "./JobDescription";
import ApplyModal from "./ApplyModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Modal overlay for a single job posting. */
export default function JobModal({ job, onClose }) {
  const [showApply, setShowApply] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  // Lock scroll + ESC to close, increment view counter once per job open
  useEffect(() => {
    if (!job) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    // Track the view (best-effort, fire-and-forget)
    axios.post(`${API}/jobs/${job.id}/view`).catch(() => {});

    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
      setShareOpen(false);
      setCopied(false);
    };
  }, [job, onClose]);

  if (!job) return null;

  const shareUrl = `${window.location.origin}/?job=${encodeURIComponent(job.id)}#careers`;
  const shareTitle = `${job.title} — Epicom Systems Ltd.`;
  const shareText = job.summary || `Now hiring: ${job.title} at Epicom Systems Ltd.`;

  const useFormApply = job.apply_method === "form";
  const externalApplyHref = (() => {
    if (job.apply_method === "url" && job.apply_value) return job.apply_value;
    const email = job.apply_value || "rod@epicom.ca";
    const subject = encodeURIComponent(`Application — ${job.title}`);
    return `mailto:${email}?subject=${subject}`;
  })();

  const handleApplyClick = (e) => {
    if (useFormApply) {
      e.preventDefault();
      setShowApply(true);
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success("Link copied to clipboard");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Couldn't copy link.");
    }
  };

  const shareTargets = [
    {
      key: "copy",
      label: copied ? "Copied" : "Copy link",
      icon: copied ? Check : LinkIcon,
      action: copyLink,
    },
    {
      key: "linkedin",
      label: "LinkedIn",
      icon: Linkedin,
      href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`,
    },
    {
      key: "facebook",
      label: "Facebook",
      icon: Facebook,
      href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`,
    },
    {
      key: "email",
      label: "Email",
      icon: Mail,
      href: `mailto:?subject=${encodeURIComponent(shareTitle)}&body=${encodeURIComponent(`${shareText}\n\n${shareUrl}`)}`,
    },
    {
      key: "sms",
      label: "SMS",
      icon: MessageCircle,
      href: `sms:?&body=${encodeURIComponent(`${shareTitle} — ${shareUrl}`)}`,
    },
  ];

  return createPortal(
    <>
      <div
        data-testid="job-modal-overlay"
        className="fixed inset-0 z-[100] flex items-center justify-center px-4 py-8 md:py-12"
        onClick={(e) => {
          if (e.target === e.currentTarget) onClose();
        }}
        style={{ animation: "jm-fade 220ms ease-out both" }}
      >
        {/* Backdrop */}
        <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" />

        {/* Card */}
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="job-modal-title"
          data-testid="job-modal"
          className="relative bg-bg-secondary border border-steel-dark w-full max-w-3xl max-h-[90vh] flex flex-col"
          style={{ animation: "jm-rise 320ms cubic-bezier(.16,.84,.34,1) both" }}
        >
          {/* Top accent bar */}
          <div className="h-[3px] bg-signal" />

          {/* Header */}
          <div className="flex items-start justify-between p-6 md:p-8 pb-4 border-b border-steel-dark">
            <div className="flex-1 min-w-0 pr-6">
              <div className="flex items-center gap-3 mb-4">
                <Briefcase className="text-signal flex-shrink-0" size={20} strokeWidth={1.5} />
                <span className="font-mono text-[10px] tracking-[0.25em] text-signal uppercase border border-signal/40 px-3 py-1">
                  Now Hiring
                </span>
              </div>
              <h2
                id="job-modal-title"
                className="heading text-2xl md:text-3xl lg:text-4xl text-white uppercase leading-tight"
              >
                {job.title}
              </h2>
              <div className="flex flex-wrap gap-x-5 gap-y-2 mt-4 font-mono text-[10px] md:text-xs tracking-[0.2em] text-steel-light uppercase">
                {job.location && (
                  <span className="inline-flex items-center gap-2">
                    <MapPin size={12} className="text-signal" />
                    {job.location}
                  </span>
                )}
                {job.employment_type && (
                  <span className="inline-flex items-center gap-2">
                    <Clock size={12} className="text-signal" />
                    {job.employment_type}
                  </span>
                )}
                {job.compensation && (
                  <span className="text-signal">{job.compensation}</span>
                )}
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              data-testid="job-modal-close"
              aria-label="Close"
              className="flex-shrink-0 -mr-2 -mt-2 p-2 text-steel-light hover:text-signal transition-colors"
            >
              <X size={22} />
            </button>
          </div>

          {/* Body — scrollable */}
          <div className="flex-1 overflow-y-auto p-6 md:p-8">
            {job.summary && (
              <p className="text-white/90 text-base md:text-lg leading-relaxed mb-6 italic border-l-2 border-signal pl-4">
                {job.summary}
              </p>
            )}
            {job.description ? (
              <JobDescription text={job.description} />
            ) : (
              <p className="text-steel-light text-sm italic">
                No additional details provided. Hit Apply to start the conversation.
              </p>
            )}
          </div>

          {/* Footer / actions */}
          <div className="flex flex-col gap-3 p-6 md:p-8 border-t border-steel-dark bg-bg-primary/60">
            {/* Share row */}
            <div className="relative flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={() => setShareOpen((v) => !v)}
                data-testid="job-modal-share-toggle"
                aria-expanded={shareOpen}
                className="inline-flex items-center gap-2 font-mono text-xs tracking-[0.2em] uppercase text-steel-light hover:text-signal border border-steel-dark hover:border-signal px-4 py-2.5 transition-colors"
              >
                <Share2 size={14} />
                Share this job
              </button>
              {shareOpen && (
                <div
                  data-testid="job-modal-share-targets"
                  className="flex flex-wrap items-center gap-2"
                  style={{ animation: "jm-fade 180ms ease-out both" }}
                >
                  {shareTargets.map((t) => {
                    const Icon = t.icon;
                    const baseCls =
                      "inline-flex items-center gap-2 font-mono text-[10px] tracking-[0.2em] uppercase text-steel-light hover:text-white hover:bg-signal/90 border border-steel-dark hover:border-signal px-3 py-2.5 transition-colors";
                    if (t.action) {
                      return (
                        <button
                          key={t.key}
                          type="button"
                          onClick={t.action}
                          data-testid={`job-share-${t.key}`}
                          className={baseCls}
                        >
                          <Icon size={13} />
                          {t.label}
                        </button>
                      );
                    }
                    return (
                      <a
                        key={t.key}
                        href={t.href}
                        target={t.key === "linkedin" || t.key === "facebook" ? "_blank" : undefined}
                        rel="noopener noreferrer"
                        data-testid={`job-share-${t.key}`}
                        className={baseCls}
                      >
                        <Icon size={13} />
                        {t.label}
                      </a>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Close + Apply */}
            <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-3">
              <button
                type="button"
                onClick={onClose}
                className="font-mono text-xs tracking-[0.2em] uppercase text-steel-light hover:text-signal border border-steel-dark hover:border-signal px-5 py-3 transition-colors"
              >
                Close
              </button>
              {useFormApply ? (
                <button
                  type="button"
                  onClick={() => setShowApply(true)}
                  data-testid="job-modal-apply"
                  className="group inline-flex items-center justify-center gap-2 bg-signal hover:bg-signal-hover text-white font-bold uppercase tracking-wider text-sm px-6 py-3 transition-colors"
                >
                  Apply Now
                  <ArrowUpRight
                    size={16}
                    className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform"
                  />
                </button>
              ) : (
                <a
                  href={externalApplyHref}
                  target={job.apply_method === "url" ? "_blank" : undefined}
                  rel={job.apply_method === "url" ? "noopener noreferrer" : undefined}
                  onClick={handleApplyClick}
                  data-testid="job-modal-apply"
                  className="group inline-flex items-center justify-center gap-2 bg-signal hover:bg-signal-hover text-white font-bold uppercase tracking-wider text-sm px-6 py-3 transition-colors"
                >
                  Apply Now
                  <ArrowUpRight
                    size={16}
                    className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform"
                  />
                </a>
              )}
            </div>
          </div>
        </div>

        <style>{`
          @keyframes jm-fade {
            from { opacity: 0; }
            to   { opacity: 1; }
          }
          @keyframes jm-rise {
            from { opacity: 0; transform: translateY(20px) scale(0.985); }
            to   { opacity: 1; transform: translateY(0)    scale(1);    }
          }
        `}</style>
      </div>

      {showApply && (
        <ApplyModal
          job={job}
          onClose={() => setShowApply(false)}
          onSubmitted={() => {
            /* Could refresh stats here later */
          }}
        />
      )}
    </>,
    document.body,
  );
}
